import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { signOut } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useTenantCollection } from '../../hooks/useTenantData';
import { useClub } from '../../contexts/ClubContext';

function AdminDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user] = useAuthState(auth);
  const { clubId: contextClubId, club } = useClub();
  
  // Use URL param as fallback if context hasn't loaded yet
  const clubId = contextClubId || searchParams.get('club');
  
  // Use tenant-scoped data hooks for dashboard stats
  const { data: players, loading: playersLoading } = useTenantCollection('players');
  const { data: teams, loading: teamsLoading } = useTenantCollection('teams');
  const { data: registrations, loading: regsLoading } = useTenantCollection('registrations');
  const { data: products, loading: productsLoading } = useTenantCollection('products');
  const { data: events, loading: eventsLoading } = useTenantCollection('events');
  
  const [profile, setProfile] = useState({ firstName: '', lastName: '', displayName: '', role: '' });
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [coachesCount, setCoachesCount] = useState(0);
  const [coachesLoading, setCoachesLoading] = useState(true);
  const isSuperAdmin = profile.role === 'super_admin';
  
  const loadingStats = playersLoading || teamsLoading || regsLoading || productsLoading || eventsLoading || coachesLoading;

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      if (user) {
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setProfile(userDoc.data());
          }
        } catch (error) {
          console.error('Error fetching profile:', error);
        }
      }
    };
    fetchProfile();
  }, [user]);

  // Fetch coaches count (need direct query as coaches are in users collection)
  useEffect(() => {
    const fetchCoaches = async () => {
      if (!clubId) return;
      try {
        const coachesSnap = await getDocs(
          query(collection(db, 'users'), where('role', '==', 'coach'), where('clubId', '==', clubId))
        );
        setCoachesCount(coachesSnap.size);
      } catch (error) {
        console.error('Error fetching coaches:', error);
      } finally {
        setCoachesLoading(false);
      }
    };
    fetchCoaches();
  }, [clubId]);

  // Calculate stats from tenant-scoped data
  const pendingRegs = registrations.filter(r => r.status === 'pending').length;
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingEvents = events.filter(e => new Date(e.date) >= today).length;

  const stats = {
    players: players.length,
    teams: teams.length,
    registrations: pendingRegs,
    products: products.length,
    events: upcomingEvents,
    coaches: coachesCount
  };

  // Get recent registrations (newest 4)
  const recentRegistrations = [...registrations]
    .sort((a, b) => {
      const dateA = a.submittedAt?.toDate?.() || new Date(a.submittedAt || 0);
      const dateB = b.submittedAt?.toDate?.() || new Date(b.submittedAt || 0);
      return dateB - dateA;
    })
    .slice(0, 4);

  const handleSaveProfile = async () => {
    if (!user) return;
    setSaving(true);
    setSaveError('');
    try {
      await setDoc(doc(db, 'users', user.uid), {
        firstName: profile.firstName,
        lastName: profile.lastName,
        displayName: `${profile.firstName} ${profile.lastName}`.trim(),
        email: user.email,
        role: 'admin',
        clubId: clubId,  // Ensure clubId is set
        updatedAt: new Date()
      }, { merge: true });
      setShowProfileModal(false);
    } catch (error) {
      console.error('Error saving profile:', error);
      setSaveError(error.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const displayName = profile.firstName && profile.lastName 
    ? `${profile.firstName} ${profile.lastName}` 
    : profile.displayName || user?.email?.split('@')[0] || 'Admin';

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/admin');
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  // Format date for display
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-TT', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="admin-dashboard">
      {/* Admin Header */}
      <header className="admin-header">
        <div className="admin-header-left">
          <span className="admin-logo">⚽</span>
          <span className="admin-title">{club?.name || 'ADMIN'} - ADMIN</span>
        </div>
        <div className="admin-header-right">
          <button 
            onClick={() => {
              console.log('Profile button clicked');
              setShowProfileModal(true);
            }} 
            className="admin-profile-btn"
            type="button"
          >
            {displayName}
          </button>
          {isSuperAdmin && (
            <button onClick={() => navigate('/super-admin')} className="admin-super-btn">
              Super Admin
            </button>
          )}
          <button onClick={handleLogout} className="admin-logout-btn">
            Logout
          </button>
        </div>
      </header>

      <div className="admin-layout">
        {/* Sidebar */}
        <aside className="admin-sidebar">
          <nav className="admin-nav">
            <a href={`/admin/dashboard?club=${clubId}`} className="admin-nav-item active">
              <span className="nav-icon">📊</span>
              Dashboard
            </a>
            <a href={`/admin/players?club=${clubId}`} className="admin-nav-item">
              <span className="nav-icon">⚽</span>
              Players
            </a>
            <a href={`/admin/teams?club=${clubId}`} className="admin-nav-item">
              <span className="nav-icon">👥</span>
              Teams
            </a>
            <a href={`/admin/schedule?club=${clubId}`} className="admin-nav-item">
              <span className="nav-icon">📅</span>
              Schedule
            </a>
            <a href={`/admin/shop?club=${clubId}`} className="admin-nav-item">
              <span className="nav-icon">🛒</span>
              Kit Shop
            </a>
            <a href={`/admin/orders?club=${clubId}`} className="admin-nav-item">
              <span className="nav-icon">📦</span>
              Orders
            </a>
            <a href={`/admin/registrations?club=${clubId}`} className="admin-nav-item">
              <span className="nav-icon">📋</span>
              Registrations
            </a>
            <a href={`/admin/coaches?club=${clubId}`} className="admin-nav-item">
              <span className="nav-icon">🏃</span>
              Coaches
            </a>
            <a href={`/admin/gallery?club=${clubId}`} className="admin-nav-item">
              <span className="nav-icon">📸</span>
              Gallery
            </a>
            <a href={`/admin/content?club=${clubId}`} className="admin-nav-item">
              <span className="nav-icon">📝</span>
              Content
            </a>
            <div className="nav-divider"></div>
            <a href={`/admin/subscription?club=${clubId}`} className="admin-nav-item">
              <span className="nav-icon">💳</span>
              Subscription
            </a>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="admin-main">
          <div className="admin-welcome">
            <h1>Welcome back, {displayName}!</h1>
            <p>Here's what's happening with {club?.name || 'your club'} today.</p>
          </div>

          {/* Stats Grid */}
          <div className="admin-stats-grid">
            <div className="admin-stat-card">
              <div className="stat-icon">⚽</div>
              <div className="stat-content">
                <span className="stat-number">{loadingStats ? '-' : stats.players}</span>
                <span className="stat-label">Spotlight Players</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon">👥</div>
              <div className="stat-content">
                <span className="stat-number">{loadingStats ? '-' : stats.teams}</span>
                <span className="stat-label">Active Teams</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon">📋</div>
              <div className="stat-content">
                <span className="stat-number">{loadingStats ? '-' : stats.registrations}</span>
                <span className="stat-label">Pending Registrations</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon">🛒</div>
              <div className="stat-content">
                <span className="stat-number">{loadingStats ? '-' : stats.products}</span>
                <span className="stat-label">Products</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon">📅</div>
              <div className="stat-content">
                <span className="stat-number">{loadingStats ? '-' : stats.events}</span>
                <span className="stat-label">Upcoming Events</span>
              </div>
            </div>
            <div className="admin-stat-card">
              <div className="stat-icon">🏃</div>
              <div className="stat-content">
                <span className="stat-number">{loadingStats ? '-' : stats.coaches}</span>
                <span className="stat-label">Coaches</span>
              </div>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="admin-activity-grid">
            {/* Recent Registrations */}
            <div className="admin-card">
              <div className="card-header">
                <h2>Recent Registrations</h2>
                <a href={`/admin/registrations?club=${clubId}`} className="view-all">View All</a>
              </div>
              <div className="card-content">
                {loadingStats ? (
                  <p className="loading-text">Loading...</p>
                ) : recentRegistrations.length === 0 ? (
                  <p className="empty-text">No registrations yet</p>
                ) : (
                  <table className="admin-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Age Group</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentRegistrations.map(reg => (
                        <tr key={reg.id}>
                          <td>{reg.playerFirstName} {reg.playerLastName}</td>
                          <td><span className="badge">{reg.ageGroup}</span></td>
                          <td>
                            <span className={`status-badge ${reg.status || 'pending'}`}>
                              {reg.status || 'pending'}
                            </span>
                          </td>
                          <td>{formatDate(reg.submittedAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* Quick Stats */}
            <div className="admin-card">
              <div className="card-header">
                <h2>Quick Links</h2>
              </div>
              <div className="card-content">
                <div className="quick-links">
                  <a href={`/admin/players?club=${clubId}`} className="quick-link">
                    <span className="link-icon">⚽</span>
                    <span>Manage Players</span>
                  </a>
                  <a href={`/admin/teams?club=${clubId}`} className="quick-link">
                    <span className="link-icon">👥</span>
                    <span>Manage Teams</span>
                  </a>
                  <a href={`/admin/schedule?club=${clubId}`} className="quick-link">
                    <span className="link-icon">📅</span>
                    <span>Manage Schedule</span>
                  </a>
                  <a href={`/admin/shop?club=${clubId}`} className="quick-link">
                    <span className="link-icon">🛒</span>
                    <span>Manage Shop</span>
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="admin-card">
            <div className="card-header">
              <h2>Quick Actions</h2>
            </div>
            <div className="quick-actions">
              <button className="quick-action-btn" onClick={() => navigate(`/admin/players?club=${clubId}`)}>
                <span>➕</span> Add Player
              </button>
              <button className="quick-action-btn" onClick={() => navigate(`/admin/schedule?club=${clubId}`)}>
                <span>📅</span> Create Event
              </button>
              <button className="quick-action-btn" onClick={() => navigate(`/admin/shop?club=${clubId}`)}>
                <span>🛒</span> Add Product
              </button>
              <button className="quick-action-btn" onClick={() => navigate(`/admin/registrations?club=${clubId}`)}>
                <span>📋</span> View Registrations
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* Profile Modal */}
      {showProfileModal && (
        <div className="modal-overlay" onClick={() => setShowProfileModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Profile</h2>
              <button className="modal-close" onClick={() => setShowProfileModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {saveError && <div className="form-error">{saveError}</div>}
              <div className="form-group">
                <label>First Name</label>
                <input
                  type="text"
                  value={profile.firstName || ''}
                  onChange={e => setProfile({...profile, firstName: e.target.value})}
                  placeholder="Enter first name"
                />
              </div>
              <div className="form-group">
                <label>Last Name</label>
                <input
                  type="text"
                  value={profile.lastName || ''}
                  onChange={e => setProfile({...profile, lastName: e.target.value})}
                  placeholder="Enter last name"
                />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  value={user?.email || ''}
                  disabled
                  className="disabled"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowProfileModal(false)}>
                Cancel
              </button>
              <button className="btn-save" onClick={handleSaveProfile} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
